import { Component, OnInit } from '@angular/core';
import { forkJoin, of, switchMap, map, catchError, Observable } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { SoldeService } from 'src/app/services/solde.service';
import { TicketService } from 'src/app/services/ticket.service';
import { BonsService } from 'src/app/services/bons.service';
import { Ticket } from 'src/app/models/ticket';

interface SoldeParTitre {
  id: number;
  label: string;
  solde: number;
}

@Component({
  selector: 'app-soldes',
  templateUrl: './soldes.component.html',
  styleUrls: ['./soldes.component.css'],
})
export class SoldesComponent implements OnInit {
  sum: number = 0;
  soldesParTitre: SoldeParTitre[] = [];
  loadError: boolean = false;
  account!: string;

  // Bons cadeaux marchands (L5) — solde distinct des titres B2B/B2C classiques :
  // un tokenId de bon n'apparaît jamais dans user.tickets, il faut le résoudre
  // via le catalogue de produits du marchand (GET /merchant/vouchers).
  bonsMarchandTotal: number = 0;
  loadingBonsMarchand: boolean = true;

  // Le total affiché en haut de page regroupe désormais titres classiques et
  // bons marchand — un getter plutôt qu'un champ, pour rester à jour quel que
  // soit l'ordre dans lequel les deux chargements (indépendants) se terminent.
  get grandTotal(): number {
    return this.sum + this.bonsMarchandTotal;
  }

  constructor(
    private soldeService: SoldeService,
    private authService: AuthService,
    private ticketService: TicketService,
    private bonsService: BonsService,
  ) {}

  ngOnInit(): void {
    this.authService
      .getUser()
      .pipe(
        switchMap((user) => {
          const acceptedTicketIds: number[] = user?.tickets || [];
          this.account = user?.account || '';

          if (!acceptedTicketIds.length || !this.account) {
            return of([] as SoldeParTitre[]);
          }

          return this.ticketService.getAllTickets().pipe(
            catchError((error) => {
              console.error('Erreur lors du chargement des tickets :', error);
              this.loadError = true;
              return of({ tickets: [] });
            }),
            switchMap((allTickets: any) => {
              const tickets: Ticket[] = Array.isArray(allTickets.tickets) ? allTickets.tickets : [];
              const accepted = tickets.filter((t) => acceptedTicketIds.includes(t.id));

              if (!accepted.length) return of([] as SoldeParTitre[]);

              return forkJoin(
                accepted.map((ticket) =>
                  this.soldeService.getBalance(ticket.id, this.account).pipe(
                    map((res) => ({
                      id: ticket.id,
                      label: ticket.label,
                      solde: Number(res?.result?.balance) || 0,
                    })),
                    catchError((error) => {
                      console.error(`Erreur solde pour le ticket ${ticket.id} :`, error);
                      this.loadError = true;
                      return of({ id: ticket.id, label: ticket.label, solde: 0 });
                    }),
                  ),
                ),
              );
            }),
          );
        }),
      )
      .subscribe(
        (soldes) => {
          this.soldesParTitre = soldes;
          this.sum = soldes.reduce((acc, s) => acc + s.solde, 0);
        },
        (error) => {
          console.error('Erreur inattendue lors du chargement des soldes :', error);
          this.loadError = true;
        },
      );

    this.loadBonsMarchandTotal();
  }

  /**
   * Solde total des bons cadeaux marchands détenus par ce marchand — la somme,
   * pour chacun de ses propres produits ACTIVE, de `totalValue` (quantity ×
   * faceValue) actuellement dans son wallet. Ce solde représente ce que le
   * marchand peut demander à se faire reverser (compensation), exactement
   * comme pour un titre B2B/B2C classique.
   */
  private loadBonsMarchandTotal(): void {
    this.authService
      .getUser()
      .pipe(
        switchMap((user) => {
          const account = user?.account || '';
          if (!account) return of([] as number[]);

          return this.bonsService.getGroups().pipe(
            catchError(() => of({ groups: [] })),
            switchMap((res: any) => {
              // Réponse groupée : on aplatit les dénominations ACTIVE de
              // tous les groupes, chacune ayant son propre tokenId/solde.
              const denominations = (res.groups || []).flatMap((g: any) =>
                (g.denominations || []).filter((d: any) => d.status === 'ACTIVE' && d.tokenId),
              );
              if (!denominations.length) return of([] as number[]);

              return forkJoin(
                denominations.map(
                  (d: any): Observable<number> =>
                    this.soldeService.getVoucherBalance(d.tokenId, account).pipe(
                      map((r: any) => Number(r?.result?.totalValue) || 0),
                      catchError(() => {
                        this.loadError = true;
                        return of(0);
                      }),
                    ),
                ),
              );
            }),
          );
        }),
      )
      .subscribe({
        next: (values: unknown) => {
          const nums = values as number[];
          this.bonsMarchandTotal = nums.reduce((acc, v) => acc + v, 0);
          this.loadingBonsMarchand = false;
        },
        error: () => {
          this.loadingBonsMarchand = false;
        },
      });
  }
}